/**
 * Production CASL ability table — only legal home for @casl/ability (ADR-0016 / #7768).
 * Access Decision packing and parity tests import from here; callers use decision.can().
 */

import { AbilityBuilder, createMongoAbility } from '@casl/ability';
import { UserRole, UserWithRole } from '@/components/authnz/types/rbac';
import type { CaslAppAbility } from './casl-types';

/**
 * Define abilities for a user based on their role(s)
 * If user has multiple roles, combines permissions from all roles
 * 
 * @param user - User object with role information (can have single role or array of roles)
 * @returns AppAbility object with defined permissions
 */
export function defineAbilitiesFor(user: UserWithRole | null): CaslAppAbility {
  const { can, cannot, build } = new AbilityBuilder<CaslAppAbility>(createMongoAbility);

  if (!user) {
    // Guest users (not authenticated) - no permissions
    return build();
  }

  // Support both single role (backward compatibility) and multiple roles
  // Create a copy to avoid mutating the original object
  let roles = Array.isArray(user.role) ? [...user.role] : [user.role];

  // Sort roles by priority (Client -> Tutor -> Practitioner -> Administrator)
  // We want to apply LEAST permissive roles FIRST, and MOST permissive roles LAST.
  // In CASL, the last rule wins. So if Client says "cannot access Dashboard"
  // and then Administrator says "can access Dashboard", the result is "can".
  //
  // CORE_ROLES is [ADMINISTRATOR, PRACTITIONER, TUTOR, CLIENT]
  // So we want to reverse this order.
  const rolePriority = {
    [UserRole.CLIENT]: 0,
    [UserRole.SALES]: 1,
    [UserRole.TUTOR]: 2,
    [UserRole.STUDENT]: 3,
    [UserRole.PRACTITIONER]: 3,
    [UserRole.PRACTITIONER_ADMINISTRATOR]: 4,
    [UserRole.ADMINISTRATOR]: 5,
  };

  roles.sort((a, b) => {
    const prioA = rolePriority[a as UserRole] ?? -1;
    const prioB = rolePriority[b as UserRole] ?? -1;
    return prioA - prioB; // Ascending priority: 0 (Client) ... 3 (Admin)
  });

  // Define permissions for each role - permissions are combined
  for (const role of roles) {
    switch (role) {
      case UserRole.ADMINISTRATOR:
        defineAdministratorAbilities(can, cannot, user);
        break;

      case UserRole.PRACTITIONER:
        definePractitionerAbilities(can, cannot, user);
        break;

      case UserRole.PRACTITIONER_ADMINISTRATOR:
        definePractitionerAdministratorAbilities(can, cannot, user);
        break;

      case UserRole.STUDENT:
        defineStudentAbilities(can, cannot, user);
        break;

      case UserRole.TUTOR:
        defineTutorAbilities(can, cannot, user);
        break;

      case UserRole.CLIENT:
        defineClientAbilities(can, cannot, user);
        break;

      case UserRole.SALES:
        defineSalesAbilities(can, cannot, user);
        break;

      default:
        // No permissions for undefined roles
        break;
    }
  }

  return build();
}

/**
 * Administrator - Full access to everything
 */
function defineAdministratorAbilities(
  can: AbilityBuilder<CaslAppAbility>['can'],
  cannot: AbilityBuilder<CaslAppAbility>['cannot'],
  user: UserWithRole
) {
  // Administrators can do everything
  can('manage', 'all');

  // Explicitly grant all common permissions to ensure they work
  can('access', 'Dashboard');
  can('read', 'Dashboard');
  can('access', 'Client');
  can('read', 'Client');
  can('create', 'Client');
  can('update', 'Client');
  can('delete', 'Client');
  can('access', 'Assessment');
  can('read', 'Assessment');
  can('create', 'Assessment');
  can('update', 'Assessment');
  can('delete', 'Assessment');
  can('access', 'PostureAnalysis');
  can('read', 'PostureAnalysis');
  can('create', 'PostureAnalysis');
  can('update', 'PostureAnalysis');
  can('delete', 'PostureAnalysis');
  can('access', 'Drill');
  can('read', 'Drill');
  can('create', 'Drill');
  can('update', 'Drill');
  can('delete', 'Drill');
  can('manage', 'DrillPlan');
  can('read', 'DrillPlan');
  can('create', 'DrillPlan');
  can('update', 'DrillPlan');
  can('delete', 'DrillPlan');
  can('manage', 'DrillPlanAssignment');
  can('read', 'DrillPlanAssignment');
  can('create', 'DrillPlanAssignment');
  can('update', 'DrillPlanAssignment');
  can('delete', 'DrillPlanAssignment');
  can('access', 'Chat');
  can('read', 'Chat');
  can('create', 'Chat');
  can('access', 'ProgressionModel');
  can('read', 'ProgressionModel');
  can('access', 'Coaching');
  can('read', 'Coaching');

  // Explicitly grant admin-specific abilities
  can('switch', 'AdminMode');
  can('manage', 'RoleManagement');
  can('manage', 'Settings');

  // Explicitly grant Agent permissions (even though 'manage all' should cover it)
  can('access', 'Agent');
  can('read', 'Agent');
  can('create', 'Agent');
  can('manage', 'Agent');

  // Subscription and Product management (admin only)
  can('manage', 'Subscription');
  can('read', 'Subscription');
  can('create', 'Subscription');
  can('update', 'Subscription');
  can('delete', 'Subscription');
  can('manage', 'Product');
  can('read', 'Product');
  can('create', 'Product');
  can('update', 'Product');
  can('delete', 'Product');

  // Client prospects (self-assessment opt-ins) — admin read/manage only
  can('manage', 'ClientProspect');
  can('read', 'ClientProspect');
  can('read', 'FunnelAnalytics');
}

/**
 * Practitioner Administrator — practitioner clinical access plus Subscription CUD.
 * Does not grant Admin Mode, RoleManagement, Product manage, or Subscription manage.
 */
function definePractitionerAdministratorAbilities(
  can: AbilityBuilder<CaslAppAbility>['can'],
  cannot: AbilityBuilder<CaslAppAbility>['cannot'],
  user: UserWithRole
) {
  definePractitionerAbilities(can, cannot, user);

  cannot('switch', 'AdminMode');
  cannot('manage', 'RoleManagement');
  cannot('manage', 'Product');
  cannot('create', 'Product');
  cannot('update', 'Product');
  cannot('delete', 'Product');
  cannot('manage', 'Subscription');

  can('read', 'Subscription');
  can('create', 'Subscription');
  can('update', 'Subscription');
  can('delete', 'Subscription');
  can('read', 'Product');
}

/**
 * Student - Same abilities as Practitioner
 */
function defineStudentAbilities(
  can: AbilityBuilder<CaslAppAbility>['can'],
  cannot: AbilityBuilder<CaslAppAbility>['cannot'],
  user: UserWithRole
) {
  // Students have the same abilities as Practitioners
  definePractitionerAbilities(can, cannot, user);
}

/**
 * Practitioner - Full application access (except admin features)
 */
function definePractitionerAbilities(
  can: AbilityBuilder<CaslAppAbility>['can'],
  cannot: AbilityBuilder<CaslAppAbility>['cannot'],
  user: UserWithRole
) {
  // Dashboard access
  can('access', 'Dashboard');
  can('read', 'Dashboard');

  // Client management
  can('manage', 'Client');
  can('read', 'Client');
  can('create', 'Client');
  can('update', 'Client');
  can('delete', 'Client');

  // Assessments
  can('manage', 'Assessment');
  can('read', 'Assessment');
  can('create', 'Assessment');
  can('update', 'Assessment');
  can('delete', 'Assessment');

  // Posture Analysis
  can('manage', 'PostureAnalysis');
  can('read', 'PostureAnalysis');
  can('create', 'PostureAnalysis');
  can('update', 'PostureAnalysis');
  can('delete', 'PostureAnalysis');

  // Drill Library
  can('manage', 'Drill');
  can('read', 'Drill');
  can('create', 'Drill');
  can('update', 'Drill');
  can('delete', 'Drill');

  can('manage', 'DrillPlan');
  can('read', 'DrillPlan');
  can('create', 'DrillPlan');
  can('update', 'DrillPlan');
  can('delete', 'DrillPlan');
  can('manage', 'DrillPlanAssignment');
  can('read', 'DrillPlanAssignment');
  can('create', 'DrillPlanAssignment');
  can('update', 'DrillPlanAssignment');
  can('delete', 'DrillPlanAssignment');

  can('manage', 'Category');
  can('read', 'Category');
  can('create', 'Category');
  can('update', 'Category');
  can('delete', 'Category');

  // Chat / Ask Harry
  can('access', 'Chat');
  can('read', 'Chat');
  can('create', 'Chat');

  // Harry AI Agent
  can('access', 'Agent');
  can('read', 'Agent');
  can('create', 'Agent');
  can('manage', 'Agent');

  // Progression Model
  can('access', 'ProgressionModel');
  can('read', 'ProgressionModel');

  // Coaching
  can('access', 'Coaching');
  can('read', 'Coaching');

  // Settings (own settings only)
  can('read', 'Settings');
  can('update', 'Settings');

  // Cannot access admin features
  cannot('switch', 'AdminMode');
  cannot('manage', 'RoleManagement');

  // Subscription and Product access (read-only for practitioners). The `cannot('manage')`
  // must come first: `manage` covers every action and the later CASL rule wins, so the
  // reverse order would deny `read` too.
  cannot('manage', 'Subscription');
  cannot('manage', 'Product');
  can('read', 'Subscription');
  can('read', 'Product');
}

/**
 * Tutor - Educational access (to be defined further)
 */
function defineTutorAbilities(
  can: AbilityBuilder<CaslAppAbility>['can'],
  cannot: AbilityBuilder<CaslAppAbility>['cannot'],
  user: UserWithRole
) {
  // For now, tutors have similar access to practitioners
  // This can be refined based on specific requirements

  // Dashboard access
  can('access', 'Dashboard');
  can('read', 'Dashboard');

  // Can view but not manage clients
  can('read', 'Client');

  // Can view assessments
  can('read', 'Assessment');
  can('read', 'PostureAnalysis');

  // Can access drill library
  can('read', 'Drill');
  can('read', 'DrillPlan');
  can('read', 'Category');

  // Chat / Ask Harry
  can('access', 'Chat');
  can('read', 'Chat');
  can('create', 'Chat');

  // Harry AI Agent (Tutors/Students can interact with agent)
  can('access', 'Agent');
  can('read', 'Agent');
  can('create', 'Agent');
  can('manage', 'Agent');

  // Progression Model
  can('access', 'ProgressionModel');
  can('read', 'ProgressionModel');

  // Coaching
  can('access', 'Coaching');
  can('read', 'Coaching');

  // Cannot modify data
  cannot('create', 'Client');
  cannot('update', 'Client');
  cannot('delete', 'Client');
  cannot('create', 'Assessment');
  cannot('update', 'Assessment');
  cannot('delete', 'Assessment');

  // Cannot access admin features
  cannot('switch', 'AdminMode');
  cannot('manage', 'RoleManagement');

  // Subscription and Product access (read-only for tutors; `cannot('manage')` first, see practitioner)
  cannot('manage', 'Subscription');
  cannot('manage', 'Product');
  can('read', 'Subscription');
  can('read', 'Product');
}

/**
 * Client - Limited access to own data only
 */
function defineClientAbilities(
  can: AbilityBuilder<CaslAppAbility>['can'],
  cannot: AbilityBuilder<CaslAppAbility>['cannot'],
  user: UserWithRole
) {
  // Clients can only access their own assessments and posture analysis

  // Can view their own assessments
  (can as any)('read', 'Assessment', { clientUserId: user.uid });
  can('access', 'Assessment');

  // Can view their own posture analysis
  (can as any)('read', 'PostureAnalysis', { clientUserId: user.uid });
  can('access', 'PostureAnalysis');

  // Can view their own client profile
  (can as any)('read', 'Client', { userId: user.uid });

  // Limited settings access (own settings only)
  can('read', 'Settings');
  can('update', 'Settings');

  // Cannot access other features
  cannot('access', 'Dashboard');
  cannot('manage', 'Drill');
  cannot('manage', 'Category');
  cannot('access', 'Chat');
  cannot('access', 'ProgressionModel');
  cannot('access', 'Coaching');
  cannot('switch', 'AdminMode');
  cannot('manage', 'RoleManagement');

  // Subscription and Product access (read-only for clients; `cannot('manage')` first, see practitioner)
  cannot('manage', 'Subscription');
  cannot('manage', 'Product');
  can('read', 'Subscription');
  can('read', 'Product');
}

/**
 * Sales — read prospects and funnel; create clients; no admin or clinical workspace
 */
function defineSalesAbilities(
  can: AbilityBuilder<CaslAppAbility>['can'],
  cannot: AbilityBuilder<CaslAppAbility>['cannot'],
  _user: UserWithRole
) {
  can('read', 'ClientProspect');
  can('access', 'ClientProspect');
  can('read', 'FunnelAnalytics');
  can('access', 'FunnelAnalytics');
  can('create', 'Client');

  can('read', 'Settings');
  can('update', 'Settings');

  cannot('switch', 'AdminMode');
  cannot('manage', 'RoleManagement');
  cannot('update', 'ClientProspect');
  cannot('delete', 'ClientProspect');
  cannot('read', 'Client');
  cannot('update', 'Client');
  cannot('delete', 'Client');
  cannot('access', 'Dashboard');
  cannot('manage', 'Subscription');
  cannot('manage', 'Product');
}

/**
 * Create a default ability for unauthenticated users
 */
export function createGuestAbility(): CaslAppAbility {
  return defineAbilitiesFor(null);
}

